import { useRef } from 'react'
import { ResponsiveSankey } from '@nivo/sankey'
import { DS_CHART_TOKENS, getDsChartColor } from '../../../utils/chartTheme'
import { DownloadPngButton } from '../../opdClaims/primitives'

const LABEL_PADDING = 16
const LINE_HEIGHT = 16
const LEFT_NODE_X_THRESHOLD = 250
const BASELINE_OFFSET = 12
const LINE_BREAK = '__BR__'
const PRIMARY_LABEL_STYLE = { fill: DS_CHART_TOKENS.ink, fontSize: 13, fontFamily: 'Inter', fontWeight: 600 }
const SECONDARY_LABEL_STYLE = { fill: DS_CHART_TOKENS.gray600, fontSize: 11, fontFamily: 'JetBrains Mono' }
const tooltipContainerStyle = {
  padding: 'var(--sp-3)',
  backgroundColor: DS_CHART_TOKENS.paper,
  color: DS_CHART_TOKENS.ink,
  border: `1px solid ${DS_CHART_TOKENS.gray300}`,
  borderRadius: 6,
  boxShadow: 'var(--sh-md)',
  fontFamily: 'Inter, sans-serif',
  fontSize: 12,
}

const tooltipValueStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  color: DS_CHART_TOKENS.ink,
  fontWeight: 600,
}

/** Renders multi-line labels (split by __BR__) as a custom Sankey layer. */
function MultiLineLabelsLayer({ nodes, width }) {
  return (
    <g>
      {nodes.map((node) => {
        const labelText = node.label || node.id || ''
        const lines = String(labelText).split(LINE_BREAK).map((s) => s.trim()).filter(Boolean)
        if (lines.length === 0) return null
        const nodeWidth = node.width ?? 0
        const nodeHeight = node.height ?? 35
        const isLeft = node.x < LEFT_NODE_X_THRESHOLD
        const textX = isLeft ? -LABEL_PADDING : nodeWidth + LABEL_PADDING
        const textAnchor = isLeft ? 'end' : 'start'
        const totalLabelHeight = lines.length * LINE_HEIGHT
        const startY = Math.max(0, (nodeHeight - totalLabelHeight) / 2) + BASELINE_OFFSET

        return (
          <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
            {lines.map((line, index) => (
              <text
                key={index}
                x={textX}
                y={startY + index * LINE_HEIGHT}
                textAnchor={textAnchor}
                style={index === 0 ? PRIMARY_LABEL_STYLE : SECONDARY_LABEL_STYLE}
              >
                {line}
              </text>
            ))}
          </g>
        )
      })}
    </g>
  )
}

const UtilizationSankeyChart = ({ sankeyData, height, classes = '', downloadName = 'utilization-flow' }) => {
  const chartRef = useRef(null)
  const { data, colors } = sankeyData

  const customLabelsLayer = (ctx) => (
    <MultiLineLabelsLayer nodes={ctx.nodes} width={ctx.width} />
  )

  return (
    <div ref={chartRef} className={`relative ${classes}`} style={{ height, overflow: 'visible' }}>
      <div className='absolute right-1 top-1 z-10' data-download-ignore='true'>
        <DownloadPngButton nodeRef={chartRef} fileName={downloadName} />
      </div>
      <ResponsiveSankey
        data={data}
        margin={{ top: 0, right: 240, bottom: 20, left: 230 }}
        align="justify"
        colors={Array.isArray(colors) ? colors.map(getDsChartColor) : colors}
        nodeOpacity={1}
        nodeThickness={35}
        nodeSpacing={32}
        nodeBorderWidth={0}
        nodeHoverOthersOpacity={0.35}
        linkOpacity={0.16}
        linkHoverOpacity={0.65}
        linkHoverOthersOpacity={0.1}
        linkContract={0.3}
        enableLabels={false}
        layers={['links', 'nodes', customLabelsLayer, 'legends']}
        nodeBorderColor={{ from: 'color', modifiers: [['darker', 0.8]] }}
        enableLinkGradient
        theme={{
          fontName: 'Inter',
          fontSize: 13,
          textColor: DS_CHART_TOKENS.gray700,
          tooltip: {
            container: tooltipContainerStyle
          }
        }}
        nodeTooltip={(node) => {
          return (
            <div style={tooltipContainerStyle}>
              <strong>
                {node.node.id}
              </strong>
              <br />
              <strong style={tooltipValueStyle}>
                {node.node.amount}
              </strong>
            </div>
          );
        }}
        label={(node) => (node.node && node.node.id) || node.id || ''}
        linkTooltip={(link) => {
          return (
            <div style={{ ...tooltipContainerStyle, display: 'flex', gap: '10px' }}>
              <div>
                {link.link.source.id}<br /><span style={tooltipValueStyle}>{link.link.source.amount}</span>
              </div>
              ➔
              <div>
                {link.link.target.id}<br /><span style={tooltipValueStyle}>{link.link.target.amount}</span>
              </div>
            </div>);
        }}
      />
    </div>
  );
}

export default UtilizationSankeyChart
